import React, { useContext } from "react";

import Container from "@material-ui/core/Container";
import Grid from "@material-ui/core/Grid";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";
import {
  AssessmentOutlined,
  CheckCircleOutline,
  ForumOutlined,
  HourglassEmpty,
} from "@material-ui/icons";

import useTickets from "../../hooks/useTickets";
import { AuthContext } from "../../context/Auth/AuthContext";
import { i18n } from "../../translate/i18n";
import { brand } from "../../styles/brand";
import Chart from "./Chart";

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(4, 5, 6),
    [theme.breakpoints.down("sm")]: { padding: theme.spacing(3, 2) },
  },
  pageHeading: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(3),
  },
  eyebrow: {
    color: brand.blue,
    fontWeight: 800,
    letterSpacing: ".13em",
    textTransform: "uppercase",
  },
  pageTitle: {
    marginTop: theme.spacing(0.5),
    color: theme.palette.type === "dark" ? theme.palette.text.primary : brand.navy,
    fontWeight: 800,
    letterSpacing: "-.04em",
  },
  pageText: { marginTop: theme.spacing(0.75), color: theme.palette.text.secondary },
  statusCard: {
    position: "relative",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    minHeight: 150,
    padding: theme.spacing(2.75),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 18,
    boxShadow: "0 18px 44px rgba(23,63,88,.09)",
  },
  iconWrap: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 40,
    height: 40,
    marginBottom: theme.spacing(1.5),
    borderRadius: 12,
    color: brand.blue,
    background: "rgba(17,165,229,.11)",
  },
  waitingIcon: { color: brand.orange, background: "rgba(222,122,43,.12)" },
  closedIcon: { color: brand.success, background: "rgba(35,184,139,.12)" },
  statusLabel: { color: theme.palette.text.secondary, fontSize: 13, fontWeight: 700 },
  statusValue: {
    marginTop: "auto",
    lineHeight: 1.05,
    color: theme.palette.type === "dark" ? theme.palette.text.primary : brand.navy,
    fontWeight: 800,
    letterSpacing: "-.04em",
  },
  accentCard: {
    color: "#FFFFFF",
    border: 0,
    background: "linear-gradient(145deg, #119CD9, #0B5C89)",
    "& $statusLabel, & $statusValue": { color: "#FFFFFF" },
    "& $iconWrap": { color: "#FFFFFF", background: "rgba(255,255,255,.14)" },
  },
  chartCard: {
    height: 340,
    marginTop: theme.spacing(2.5),
    padding: theme.spacing(3),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 20,
    boxShadow: "0 18px 44px rgba(23,63,88,.09)",
  },
}));

const Dashboard = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const userQueueIds = user?.queues?.map((queue) => queue.id) || [];
  const queueIds = JSON.stringify(userQueueIds);

  const { count: openCount } = useTickets({
    status: "open",
    showAll: "true",
    withUnreadMessages: "false",
    queueIds,
  });
  const { count: waitingCount } = useTickets({
    status: "pending",
    showAll: "true",
    withUnreadMessages: "false",
    queueIds,
  });
  const { count: closedCount } = useTickets({
    status: "closed",
    showAll: "true",
    withUnreadMessages: "false",
    queueIds,
  });

  const today = new Intl.DateTimeFormat("es-MX", {
    weekday: "long",
    day: "numeric",
    month: "long",
  }).format(new Date());

  const cards = [
    {
      label: i18n.t("dashboard.messages.inAttendance.title"),
      value: openCount,
      icon: <ForumOutlined />,
    },
    {
      label: i18n.t("dashboard.messages.waiting.title"),
      value: waitingCount,
      icon: <HourglassEmpty />,
      iconClass: classes.waitingIcon,
    },
    {
      label: i18n.t("dashboard.messages.closed.title"),
      value: closedCount,
      icon: <CheckCircleOutline />,
      iconClass: classes.closedIcon,
    },
    {
      label: "Solicitudes totales",
      value: openCount + waitingCount + closedCount,
      icon: <AssessmentOutlined />,
      accent: true,
    },
  ];

  return (
    <Container maxWidth={false} className={classes.container}>
      <div className={classes.pageHeading}>
        <div>
          <Typography variant="caption" className={classes.eyebrow}>
            Centro de atención · {today}
          </Typography>
          <Typography variant="h4" className={classes.pageTitle}>
            Resumen de conversaciones
          </Typography>
          <Typography className={classes.pageText}>
            Hola, {user?.name || "equipo"}. Este es el estado actual de la atención.
          </Typography>
        </div>
      </div>

      <Grid container spacing={2}>
        {cards.map((card) => (
          <Grid item xs={12} sm={6} md={3} key={card.label}>
            <Paper
              className={`${classes.statusCard} ${card.accent ? classes.accentCard : ""}`}
              elevation={0}
            >
              <div className={`${classes.iconWrap} ${card.iconClass || ""}`}>{card.icon}</div>
              <Typography variant="body2" className={classes.statusLabel}>
                {card.label}
              </Typography>
              <Typography variant="h3" className={classes.statusValue}>
                {card.value}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Paper className={classes.chartCard} elevation={0}>
        <Chart />
      </Paper>
    </Container>
  );
};

export default Dashboard;
